<script lang="ts">
  /**
   * フィルタチップ。 旧 `src/components/crm/FilterChip.svelte` のコールバック版。
   *
   * 旧版は `window.location` に直接書き込んで遷移するが、 rebuild は親が保持
   * する `query` state を `onChange` で更新するパターンに統一されているため、
   * このチップも `onApply(next)` コールバックを呼ぶだけにする。
   *
   * - mode='single': ラジオ的な単一選択。 選択した瞬間に確定して `onApply(value)`。
   * - mode='multi': チェックボックス的な複数選択。 「適用」 ボタンで `onApply(values)`。
   *
   * 空にクリアした場合、 single は `null`、 multi は `[]` を返す。
   */
  import { onMount, onDestroy } from 'svelte';

  interface Option {
    value: string;
    label: string;
  }

  interface PropsSingle {
    label: string;
    mode: 'single';
    options: Option[];
    currentValue: string | null;
    allLabel?: string;
    variant?: 'default' | 'table-header';
    onApply: (value: string | null) => void;
  }

  interface PropsMulti {
    label: string;
    mode: 'multi';
    options: Option[];
    currentValue: string[];
    onApply: (values: string[]) => void;
    allLabel?: string;
    variant?: 'default' | 'table-header';
  }

  type Props = PropsSingle | PropsMulti;

  const {
    label,
    mode,
    options,
    currentValue,
    onApply,
    allLabel = '全て',
    variant = 'default',
  }: Props = $props();

  let open = $state(false);
  let containerEl: HTMLDivElement | null = $state(null);

  const singleValue = $derived(
    mode === 'single' ? ((currentValue as string | null) ?? '') : '',
  );
  const multiValues = $derived<string[]>(
    mode === 'multi' ? ((currentValue as string[]) ?? []) : [],
  );

  // multi 用の編集中バッファ。 open する瞬間に同期する。
  let draft = $state<string[]>([]);

  const selectedCount = $derived(
    mode === 'multi' ? multiValues.length : singleValue ? 1 : 0,
  );

  const summary = $derived.by(() => {
    if (mode === 'single') {
      if (!singleValue) return label;
      const opt = options.find((o) => o.value === singleValue);
      return opt ? `${label}: ${opt.label}` : `${label}: ${singleValue}`;
    }
    if (multiValues.length === 0) return label;
    if (multiValues.length === 1) {
      const opt = options.find((o) => o.value === multiValues[0]);
      return opt ? `${label}: ${opt.label}` : `${label}: ${multiValues[0]}`;
    }
    return `${label} (${multiValues.length})`;
  });

  function toggleOpen(): void {
    open = !open;
    if (open && mode === 'multi') {
      draft = [...multiValues];
    }
  }

  function close(): void {
    open = false;
  }

  function handleDocClick(ev: MouseEvent): void {
    if (!open) return;
    if (containerEl && !containerEl.contains(ev.target as Node)) {
      close();
    }
  }

  onMount(() => {
    document.addEventListener('click', handleDocClick);
  });
  onDestroy(() => {
    document.removeEventListener('click', handleDocClick);
  });

  function selectSingle(value: string): void {
    if (mode !== 'single') return;
    (onApply as (v: string | null) => void)(value);
    close();
  }

  function clearSingle(): void {
    if (mode !== 'single') return;
    (onApply as (v: string | null) => void)(null);
    close();
  }

  function toggleMulti(value: string, checked: boolean): void {
    const next = [...draft];
    const idx = next.indexOf(value);
    if (checked && idx < 0) next.push(value);
    if (!checked && idx >= 0) next.splice(idx, 1);
    draft = next;
  }

  function applyMulti(): void {
    if (mode !== 'multi') return;
    (onApply as (vs: string[]) => void)(draft);
    close();
  }

  function clearMulti(): void {
    if (mode !== 'multi') return;
    (onApply as (vs: string[]) => void)([]);
    close();
  }
</script>

<div
  class="filter-chip"
  class:table-header={variant === 'table-header'}
  bind:this={containerEl}
>
  <button
    type="button"
    class="chip-btn"
    class:active={selectedCount > 0}
    onclick={toggleOpen}
    aria-haspopup="true"
    aria-expanded={open}
  >
    {summary}
    <span class="caret" aria-hidden="true">▾</span>
  </button>

  {#if open}
    <div class="popover" role="dialog">
      {#if mode === 'single'}
        <ul class="opts">
          <li>
            <button type="button" class="opt-row" onclick={clearSingle}>
              <span class="dot" class:checked={!singleValue}>●</span>
              <span>{allLabel}</span>
            </button>
          </li>
          {#each options as opt (opt.value)}
            <li>
              <button
                type="button"
                class="opt-row"
                onclick={() => selectSingle(opt.value)}
              >
                <span class="dot" class:checked={singleValue === opt.value}
                  >●</span
                >
                <span>{opt.label}</span>
              </button>
            </li>
          {/each}
        </ul>
      {:else}
        <ul class="opts">
          {#each options as opt (opt.value)}
            <li>
              <label class="opt-row">
                <input
                  type="checkbox"
                  checked={draft.includes(opt.value)}
                  onchange={(e) =>
                    toggleMulti(
                      opt.value,
                      (e.currentTarget as HTMLInputElement).checked,
                    )}
                />
                <span>{opt.label}</span>
              </label>
            </li>
          {/each}
        </ul>
        <div class="popover-actions">
          <button type="button" class="link" onclick={clearMulti}>クリア</button
          >
          <button type="button" class="primary" onclick={applyMulti}
            >適用</button
          >
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .filter-chip {
    position: relative;
    display: inline-block;
    max-width: 100%;
  }
  .chip-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border: 1px solid #d1d5db;
    border-radius: 999px;
    background: #ffffff;
    font: inherit;
    font-size: 13px;
    color: #374151;
    cursor: pointer;
    max-width: 100%;
  }
  .chip-btn:hover {
    background: #f9fafb;
  }
  .chip-btn.active {
    border-color: #2563eb;
    background: #eff6ff;
    color: #1d4ed8;
  }
  .table-header .chip-btn {
    gap: 4px;
    justify-content: center;
    min-width: 0;
    padding: 3px 7px;
    border-color: transparent;
    border-radius: 4px;
    background: transparent;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.25;
    text-align: center;
    white-space: normal;
  }
  .table-header .chip-btn:hover {
    background: #eef2f7;
  }
  .table-header .chip-btn.active {
    border-color: #bfdbfe;
    background: #eff6ff;
    color: #1d4ed8;
  }
  .caret {
    font-size: 10px;
    color: #6b7280;
  }
  .popover {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 4px;
    min-width: 220px;
    max-height: 320px;
    overflow: auto;
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    z-index: 50;
    padding: 6px;
  }
  .opts {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .opt-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 4px;
    width: 100%;
    background: transparent;
    border: 0;
    font: inherit;
    font-size: 13px;
    text-align: left;
    color: inherit;
    cursor: pointer;
  }
  .opt-row:hover {
    background: #f3f4f6;
  }
  .dot {
    width: 14px;
    text-align: center;
    color: #d1d5db;
    font-size: 14px;
  }
  .dot.checked {
    color: #2563eb;
  }
  .popover-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 6px 4px 0;
    border-top: 1px solid #e5e7eb;
    margin-top: 6px;
  }
  .popover-actions button {
    padding: 4px 12px;
    border-radius: 4px;
    font: inherit;
    font-size: 12px;
    cursor: pointer;
    border: 1px solid #d1d5db;
    background: #ffffff;
  }
  .popover-actions .primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
  }
  .popover-actions .link {
    border: 0;
    background: transparent;
    color: #6b7280;
  }
</style>
